import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { getCategories } from '../data/products';
import { useState, useEffect } from 'react';

interface CategoryWithImage {
  name: string;
  image: string;
}

export const CategorySection = () => {
  const [categories, setCategories] = useState<CategoryWithImage[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categoriesData = await getCategories();
        console.log('Raw categories data:', categoriesData);
        
        // Map categories to their corresponding image folders
        const categoryImageMap: { [key: string]: string } = {
          'baby': '/flip_images_baby/flip_0001.jpeg',
          'handloom': '/flip_images_handloom/flip_0001.jpeg',
          'pant': '/flip_images_pant/flip_0001.jpeg',
          'shirt': '/flip_images_shirt/flip_0001.jpeg',
          'kurta': '/flip_images_handloom/flip_0001.jpeg', // Using handloom as fallback
          'tshirt': '/flip_images_shirt/flip_0001.jpeg', // Using shirt as fallback
          'pyjama': '/flip_images_baby/flip_0001.jpeg', // Using baby as fallback
        };
        
        const categoriesWithImages: CategoryWithImage[] = categoriesData.map(category => ({
          name: category,
          image: categoryImageMap[category] || '/images/placeholder.png'
        }));
        
        console.log('Final categories with images:', categoriesWithImages);
        setCategories(categoriesWithImages);
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading categories:', error);
        setIsLoading(false);
      }
    };
    loadCategories();
  }, []);

  return (
    <section className="section-padding bg-white">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Shop by Category
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Explore our diverse collection of premium products across different categories.
          </p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : categories.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <p className="text-gray-500 text-lg">No categories available at the moment.</p>
            </div>
          </div>
        ) : (
          <div className="flex justify-center">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-6 justify-items-center max-w-7xl">
              {categories.map((category) => (
                <Link
                  key={category.name}
                  to={`/shop?category=${encodeURIComponent(category.name)}`}
                  className="group relative overflow-hidden rounded-lg bg-gray-100 aspect-square hover:shadow-lg transition-all duration-300"
                >
                  <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat group-hover:scale-105 transition-transform duration-300"
                    style={{
                      backgroundImage: `url(${category.image}), url('/images/placeholder.png')`,
                    }}
                  >
                    <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all duration-300" />
                  </div>
                  
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white">
                      <h3 className="text-lg font-semibold capitalize mb-2 group-hover:scale-105 transition-transform duration-300">
                        {category.name}
                      </h3>
                      <div className="flex items-center justify-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-sm">Shop Now</span>
                        <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="text-center mt-8">
          <Link
            to="/shop"
            className="btn-primary btn-lg inline-flex items-center space-x-2"
          >
            <span>View All Categories</span>
            <ArrowRight className="h-5 w-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};
